import { ref, readonly } from 'vue'

/**
 * 全局WebSocket单例 - 整个应用共享一个WebSocket连接
 * 在App.vue中初始化一次，所有页面都可以使用
 */

const ws = ref(null)
const abnormalAlerts = ref([])
const allAlarms = ref([]) // 存储所有原始告警数据
const devicePositions = ref(new Map()) // 设备位置数据，key为device_id
const wsHeartbeatTimer = ref(null)
const alarmCallbacks = ref([]) // 告警回调函数列表
let isInitialized = false

/**
 * 初始化全局WebSocket连接
 * 只调用一次，后续调用会跳过
 */
export function initGlobalWebSocket() {
  if (isInitialized) {
    console.log('WebSocket已初始化，跳过重复初始化')
    return
  }

  isInitialized = true
  connectWebSocket()
}

/**
 * 连接WebSocket
 */
function connectWebSocket() {
  ws.value = new WebSocket('ws://121.22.5.79:8282')
  let info = JSON.parse(localStorage.getItem('userInfo') || '{}')

  ws.value.onopen = (e) => {
    console.log('WebSocket连接成功')
    // 发送绑定请求
    if (info.screen_group_id) {
      const params = {
        type: 'bind',
        screen_group_id: info.screen_group_id // 机构数据大屏分组ID
      }
      ws.value.send(JSON.stringify(params))
    }

    // 发送心跳 - 每30秒发送一次
    wsHeartbeatTimer.value = setInterval(() => {
      if (ws.value && ws.value.readyState === WebSocket.OPEN) {
        ws.value.send(JSON.stringify({
          type: 'ping',
          screen_group_id: info.screen_group_id
        }))
      }
    }, 30000)
  }

  ws.value.onclose = (e) => {
    console.log('WebSocket链接已断开，3秒后尝试重连...')
    // 3秒后尝试重连
    setTimeout(() => {
      connectWebSocket()
    }, 3000)
  }

  ws.value.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data)
      // console.log('接收到的WebSocket消息:', data)

      // 处理告警类型数据 - 数据结构在 data.data 中
      if (data.type === "alarm" && data.data && data.data.alarm_rule) {
        const alertData = data.data
        
        // 打印原始告警数据
        // console.log('===== WebSocket 原始告警数据 =====')
        // console.log('完整数据:', data)
        // console.log('告警时间:', alertData.alarm?.ts_text || '暂无')
        // console.log('设备名称:', alertData.device?.name || '暂无')
        // console.log('告警规则:', alertData.alarm_rule?.name || '暂无')
        // console.log('告警级别:', alertData.alarm?.level_text || alertData.alarm?.level || '暂无')
        // console.log('告警状态:', alertData.alarm?.status === '1' ? '未处理' : '已处理')
        // console.log('当前属性值:', alertData.attributes)
        // console.log('===== 原始告警数据结束 =====\n')
        
        // 存储所有原始告警数据
        allAlarms.value = [data, ...allAlarms.value].slice(0, 100) // 最多保留100条
        
        // 触发所有注册的回调函数
        alarmCallbacks.value.forEach(callback => {
          try {
            callback(data)
          } catch (error) {
            console.error('告警回调执行失败:', error)
          }
        })
        
        const alarmRuleId = alertData.alarm_rule.id  // 用于去重的ID

        // 构建告警项
        const alertItem = {
          id: alertData.alarm.ts,  // 使用时间戳作为唯一 ID
          ruleId: alarmRuleId,     // 告警规则 ID 用于去重
          title: alertData.alarm_rule.notify_text || alertData.alarm_rule.name || '告警通知',
          deviceId: alertData.device.id || '',
          userName: alertData.device.name || '',
          level: alertData.alarm.level === '3' ? 'high' : alertData.alarm.level === '2' ? 'medium' : 'low',
          time: alertData.alarm.ts_time || '暂无',
          status: alertData.alarm.status === '1' ? 'unresolved' : 'resolved',
          location: '',  // 设备信息中没有位置，可后续补充
          // 添加 device 字段，方便模板直接访问设备信息
          device: {
            id: alertData.device.id || '',
            name: alertData.device.name || '',
            device_key: alertData.device.device_key || '',
            device_type: alertData.device.device_type || '',
            device_type_text: alertData.device.device_type_text || '未知设备'
          },
          // 保留原始数据
          rawData: alertData
        }

        // 检查是否重复（根据 alarm_rule.id 判断）
        const existingIndex = abnormalAlerts.value.findIndex(alert => alert.ruleId === alarmRuleId)

        if (existingIndex !== -1) {
          // 找到重复数据，用新数据替换旧数据
          console.log('告警已存在，替换旧数据:', alarmRuleId)
          const newAlerts = [...abnormalAlerts.value]
          newAlerts[existingIndex] = alertItem
          // 将更新后的项移到列表头部
          abnormalAlerts.value = [alertItem, ...newAlerts.filter((_, idx) => idx !== existingIndex)]
        } else {
          // 不重复，添加到告警列表头部
          abnormalAlerts.value = [alertItem, ...abnormalAlerts.value]
        }

        // 限制最大显示15条告警
        const MAX_ALERTS = 15
        if (abnormalAlerts.value.length > MAX_ALERTS) {
          abnormalAlerts.value = abnormalAlerts.value.slice(0, MAX_ALERTS)
        }

        console.log('更新后的告警列表:', abnormalAlerts.value)
        console.log('当前告警数量:', abnormalAlerts.value.length)
      }

      // 处理设备位置类型数据 - 实时更新设备位置
      if (data.type === "position") {
        const positionData = {
          device_id: data.device_id,
          device_name: data.device_name || '',
          device_key: data.device_key || '',
          location: data.location || { lat: '', lng: '' },
          ts: data.ts || '',
          groups: data.groups || []
        }

        // 更新设备位置数据
        devicePositions.value.set(data.device_id, positionData)
        // console.log('更新设备位置:', positionData)
      }
    } catch (error) {
      console.error('处理WebSocket消息失败:', error)
    }
  }

  ws.value.onerror = (e) => {
    console.log('WebSocket链接错误')
  }
}

/**
 * 手动发送WebSocket消息
 * @param {Object} message - 要发送的消息对象
 */
export function sendWebSocketMessage(message) {
  if (ws.value && ws.value.readyState === WebSocket.OPEN) {
    ws.value.send(JSON.stringify(message))
    console.log('发送WebSocket消息:', message)
  } else {
    console.warn('WebSocket未连接，无法发送消息')
  }
}

/**
 * 关闭WebSocket连接
 */
export function closeGlobalWebSocket() {
  if (wsHeartbeatTimer.value) {
    clearInterval(wsHeartbeatTimer.value)
    wsHeartbeatTimer.value = null
  }
  if (ws.value) {
    ws.value.close()
    ws.value = null
  }
  isInitialized = false
}

/**
 * 添加告警到列表
 * @param {Object} alertItem - 告警项
 */
export function addAlert(alertItem) {
  abnormalAlerts.value = [alertItem, ...abnormalAlerts.value]
  const MAX_ALERTS = 15
  if (abnormalAlerts.value.length > MAX_ALERTS) {
    abnormalAlerts.value = abnormalAlerts.value.slice(0, MAX_ALERTS)
  }
}

/**
 * 清除所有告警
 */
export function clearAlerts() {
  abnormalAlerts.value = []
}

/**
 * 获取告警数量统计
 */
export function getAlertStats() {
  return {
    total: abnormalAlerts.value.length,
    high: abnormalAlerts.value.filter(alert => alert.level === 'high').length,
    medium: abnormalAlerts.value.filter(alert => alert.level === 'medium').length,
    low: abnormalAlerts.value.filter(alert => alert.level === 'low').length
  }
}

/**
 * 使用全局WebSocket
 * 在页面中使用此hook获取WebSocket实例
 * @returns {Object} 包含 ws, abnormalAlerts, sendWebSocketMessage
 */
export function useGlobalWebSocket() {
  return {
    ws: readonly(ws),      // 只读，防止直接修改
    abnormalAlerts,         // 直接导出，保持响应式
    allAlarms,              // 所有原始告警数据
    devicePositions,        // 设备位置数据，用于实时更新地图
    sendWebSocketMessage,
    getAlertStats,
    onAlarmReceived         // 实时告警回调
  }
}

export { abnormalAlerts, allAlarms, devicePositions }

/**
 * 注册实时告警回调
 * @param {Function} callback - 回调函数，参数为原始告警数据
 */
export function onAlarmReceived(callback) {
  if (typeof callback === 'function') {
    alarmCallbacks.value.push(callback)
    console.log('已注册告警回调，当前回调数量:', alarmCallbacks.value.length)
  }
}
